import * as React from "react";
import { useMemo } from "react";
import { Formik, Field, useFormikContext } from "formik";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import usePermissions from "hooks/usePermissions";
import { Row } from "common/components";
import { Button } from "components/bootstrap";
import { FormikSelect, FormikInput, FormikInputList } from "security-app/components/common/FormikComponents";
import { customFieldValidation } from "./utils";
const INIT_CUSTOM_FIELD = {
  name: "",
  type: "",
  values: []
};
const FIELD_TYPE_OPTIONS = [
  { value: "STRING", labelKey: "custom_field_form.type_string" },
  { value: "DATE", labelKey: "custom_field_form.type_date" },
  { value: "NUMBER", labelKey: "custom_field_form.type_number" }
];
const NewCustomFieldFormWrapper = styled.div`
  position: sticky;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  top: -16px;
  margin: 0;
  background-color: ${({ theme }) => theme.colors.global.background};
  z-index: 1;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.input.border};
  box-sizing: border-box;
  width: 100%;
`;
function CustomFieldForm({ customField = void 0, setShowNewCustomFieldForm, setSelectedField }) {
  var _a;
  const { t } = useTranslation("enterprise_security_assets");
  const { values: assetValues, setFieldValue } = useFormikContext();
  const { isPermitted } = usePermissions();
  const canEdit = isPermitted("asset:edit");
  const isEditing = useMemo(() => !!customField, [customField]);
  const fieldTypeOptions = useMemo(
    () => FIELD_TYPE_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );
  const initialValues = useMemo(() => {
    if (customField) {
      const fieldName = Object.keys(customField)[0];
      const fieldDetails = customField[fieldName];
      return {
        name: fieldName,
        type: fieldDetails.type || "",
        values: fieldDetails.values || []
      };
    }
    return INIT_CUSTOM_FIELD;
  }, [customField]);
  const onCancel = (resetForm) => {
    var _a2;
    resetForm();
    if (isEditing) {
      setFieldValue("details.custom_fields", {
        ...(_a2 = assetValues.details) == null ? void 0 : _a2.custom_fields,
        ...customField
      });
      setSelectedField(void 0);
    }
    setShowNewCustomFieldForm(false);
  };
  const onClickSubmit = (submitForm, resetForm) => {
    submitForm();
    resetForm();
    if (isEditing) setSelectedField(void 0);
    setShowNewCustomFieldForm(false);
  };
  const onSubmit = (newValues) => {
    var _a2;
    const newField = { [newValues.name]: { type: newValues.type, values: newValues.values } };
    setFieldValue("details.custom_fields", { ...(_a2 = assetValues.details) == null ? void 0 : _a2.custom_fields, ...newField });
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      onSubmit,
      validate: customFieldValidation(((_a = assetValues.details) == null ? void 0 : _a.custom_fields) || {})
    },
    ({ values, submitForm, touched, errors, resetForm, isValid }) => /* @__PURE__ */ React.createElement(NewCustomFieldFormWrapper, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
      Field,
      {
        component: FormikInput,
        label: t("custom_field_form.name_label"),
        name: "name",
        id: "name",
        type: "text",
        placeholder: t("custom_field_form.name_placeholder"),
        value: values.name,
        required: true,
        error: (touched == null ? void 0 : touched.name) && (errors == null ? void 0 : errors.name) ? errors == null ? void 0 : errors.name : void 0,
        bsStyle: (touched == null ? void 0 : touched.name) && (errors == null ? void 0 : errors.name) ? "error" : null,
        disabled: !canEdit
      }
    ), /* @__PURE__ */ React.createElement(
      Field,
      {
        component: FormikSelect,
        label: t("custom_field_form.type_label"),
        name: "type",
        id: "type",
        placeholder: t("custom_field_form.type_placeholder"),
        value: values.type,
        options: fieldTypeOptions,
        maxMenuHeight: 200,
        menuPlacement: "top",
        disabled: !canEdit,
        error: (touched == null ? void 0 : touched.type) && (errors == null ? void 0 : errors.type) ? errors == null ? void 0 : errors.type : "",
        bsStyle: (touched == null ? void 0 : touched.type) && (errors == null ? void 0 : errors.type) ? "error" : null,
        required: true
      }
    )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
      Field,
      {
        component: FormikInputList,
        label: t("custom_field_form.values_label"),
        name: "values",
        id: "values",
        placeholder: t("custom_field_form.values_placeholder"),
        help: t("custom_field_form.list_help"),
        values: values.values,
        error: (touched == null ? void 0 : touched.values) && (errors == null ? void 0 : errors.values) ? errors == null ? void 0 : errors.values : "",
        bsStyle: (touched == null ? void 0 : touched.values) && (errors == null ? void 0 : errors.values) ? "error" : null,
        isDisabled: !canEdit,
        required: true
      }
    )), /* @__PURE__ */ React.createElement(Row, { $align: "center", $justify: "flex-end" }, /* @__PURE__ */ React.createElement(Button, { bsSize: "small", onClick: () => onCancel(resetForm) }, t("common:actions.cancel")), /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: () => onClickSubmit(submitForm, resetForm),
        bsSize: "small",
        bsStyle: "primary",
        disabled: !isValid
      },
      isEditing ? t("custom_field_form.save_changes") : t("custom_field_form.add_field")
    )))
  );
}
export default CustomFieldForm;
